import { Trans, useTranslation } from 'react-i18next';
import { match, Pattern } from 'ts-pattern';

import type { ForumPostSystemMessage as TForumPostSystemMessage } from '~/api/forum/types';

import { Icon, type IconName, Link } from '~/components';
import { routes } from '~/providers/RouterProvider/router.routes';

import { boldStyle, systemMessageStyle } from './ForumPostSystemMessage.css';

type Props = {
  systemMessage: TForumPostSystemMessage;
};

export const ForumPostSystemMessage = ({ systemMessage }: Props) => {
  const { t } = useTranslation(['forum']);

  return (
    <div className={systemMessageStyle}>
      <Icon
        name={match(systemMessage.type)
          .returnType<IconName>()
          .with('LockedByModerator', () => 'Lock')
          .with('MovedByModerator', () => 'ArrowRight')
          .with('CommentDeletedByModerator', () => 'Trash')
          .with('CommentDeletedByUser', () => 'Trash')
          .exhaustive()}
        size={20}
      />

      <span>
        {match(systemMessage)
          .with({ type: 'LockedByModerator', data: Pattern.select() }, (data) =>
            t('forum:post.systemMessages.LockedByModerator', {
              moderator: data.firstName ?? t('forum:post.systemMessages.unknownModerator'),
            }),
          )
          .with({ type: 'MovedByModerator', data: Pattern.select() }, (data) => (
            <Trans
              components={{ bold: <span className={boldStyle} /> }}
              i18nKey="forum:post.systemMessages.MovedByModerator"
              values={{
                moderator: data.firstName ?? t('forum:post.systemMessages.unknownModerator'),
                oldTag: t(`forum:tags.${data.oldTag}.name`),
                newTag: t(`forum:tags.${data.newTag}.name`),
              }}
            />
          ))
          .with({ type: 'CommentDeletedByModerator', data: Pattern.select() }, (data) => (
            <Trans
              components={{ guidelinesLink: <Link to={routes.forumGuidelines} /> }}
              i18nKey="forum:post.systemMessages.CommentDeletedByModerator"
              values={{ moderator: data.firstName ?? t('forum:post.systemMessages.unknownModerator') }}
            />
          ))
          .with({ type: 'CommentDeletedByUser', data: Pattern.select() }, () => t('forum:post.systemMessages.CommentDeletedByUser'))
          .exhaustive()}
      </span>
    </div>
  );
};
